<template>
	<header>
		<nav class="main fix-on-scroll" :class="page && page.headingImage ? '' : 'no-slider'">
			<div class="row">
				<div class="nav-column5 menu row flex">
					<div class="menu-button button" @click="toggleMenu">
						<div class="hamburger">
							<span :class="{ 'top-line': true, active: showMenu }"></span>
							<span :class="{ 'middle-line': true, active: showMenu }"></span>
							<span :class="{ 'bottom-line': true, active: showMenu }"></span>
						</div>

						<div v-if="showMenu">
							<span class="label">{{ $t('close') }}</span>
						</div>
						<div v-else>
							<span class="label">{{ $t('menu') }}</span>
						</div>
					</div>
				</div>
				<div class="logo-container">
					<nuxt-link
						aria-label="Go to homepage"
						to="/"
						:class="{ grow: showMenu }"
						class="logo-wrapper"
						@click="handleLogoClick"
					>
						<span class="logo" v-html="logo" />
					</nuxt-link>
				</div>
				<div class="nav-column5 navbuttons row flex">
					<div class="nav-buttons">
						<lang-select :items="page.langmenu" />
						<div class="nav-contact-icons">
							<a :href="`mailto:${defaults[locale].website.email}`">
								<font-awesome-icon icon="fa-sharp fa-regular fa-envelope" size="1x" />
							</a>
							<a :href="`tel:${$phoneRaw(defaults[locale].website.phone)}`">
								<font-awesome-icon icon="fa-sharp fa-regular fa-phone" size="1x" />
							</a>
						</div>
						<book-button class="button button-cta reserve-now right" aria-label="Reserve now" href="#">
							<font-awesome-icon icon="fa-light fa-calendar-days" size="1x" />

							<span>{{ defaults[locale].website.book }}</span>
						</book-button>
					</div>
				</div>
				<div :class="{ transparent: showMenu }" class="menubar-background"></div>
			</div>
			<div class="nav-container" :class="{ active: showMenu }">
				<div class="row">
					<ul class="main-nav">
						<li
							v-for="item in topmenu[locale]"
							:key="item.filename"
							:class="{ 'has-dropdown': item.submenu.length }"
						>
							<nuxt-link
								:to="`${item.filename}`"
								:class="{ active: isActiveLink(item) }"
								@click="hideMenu"
							>
								{{ item.header }}
							</nuxt-link>
							<submenu
								v-if="item.submenu.length"
								:hide-all="!showMenu"
								:item="item"
								:subitems="item.submenu"
								@hide-menu="hideMenu"
							/>
						</li>
						<!-- <li v-if="page" class="lang-nav">
							<div v-for="item in page.langmenu" :key="`${item.filename}-${item.language}`">
								<nuxt-link
									class="flag"
									:class="item.language"
									:to="`${item.filename}`"
									@click="hideMenu"
								>
									<span>{{ $t(item.language) }}</span>
								</nuxt-link>
							</div>
						</li> -->
					</ul>
					<div class="nav-contact">
						<a :href="`tel:${$phoneRaw(defaults[locale].website.phone)}`">
							<font-awesome-icon icon="fa-sharp fa-regular fa-phone" size="1x" />

							<span>{{ defaults[locale].website.phone }}</span>
						</a>

						<a :href="`mailto:${defaults[locale].website.email}`">
							<font-awesome-icon icon="fa-sharp fa-regular fa-envelope" size="1x" />

							<span>{{ defaults[locale].website.email }}</span>
						</a>
						<social-media :socials="socials" />
					</div>
				</div>
			</div>
		</nav>
	</header>
</template>

<script setup>
import logo from '~/assets/images/logo.svg?raw';

const { page, fetchPage } = usePage();
const { defaults, socials, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const localeRoute = useLocaleRoute();
const route = useRoute();
const isActiveLink = (item) => {
	return route.path === item.filename || route.path === `/${item.filename}`;
};

await fetchPage();
await fetchDefaults();

const { data: topmenu } = await useWebsiteFetch('topmenu');

const showMenu = ref(false);

const toggleMenu = () => {
	showMenu.value = !showMenu.value;
	document.body.classList.toggle('locked', showMenu.value);
};

const hideMenu = () => {
	showMenu.value = false;
	document.body.classList.remove('locked');
};

const handleLogoClick = () => {
	if (showMenu.value) {
		hideMenu();
	}

	if (route.name?.startsWith('index')) {
		window.scrollTo(0, 0);
	} else {
		const route = localeRoute('index');
		navigateTo(route.fullPath);
	}
};
</script>

<style lang="scss" scoped>
.logo-wrapper {
	display: flex;
	align-items: center;
	justify-content: center;
	position: absolute;
	width: 100px;
	inset: 0;
	margin: auto;
	animation: fade-from-top 1s ease;
	margin-top: 45px;
	z-index: 3;

	:deep(.logo svg) {
		transform: scale(1.2);
		transition: all 0.3s ease-in-out;
		fill: var(--color-haze);
	}
}

nav.main {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 100;
	padding: 30px 0;
	background: transparent;
	overflow: visible;
	transition: all 0.4s ease-in-out;
	height: 100px;

	.nav-column5 {
		transition: all 0.4s ease-in-out;
		width: calc(100% / 12 * 5);
	}

	.logo-container {
		transition: all 0.4s ease-in-out;
		width: calc(100% / 12 * 2);
	}
}

nav .row {
	overflow: visible;
	z-index: 102;
	position: relative;
}

.menubar-background {
	width: calc(100%);
	height: 88px;
	position: absolute;
	background: none;
	background: var(--color-velvet-hush);
	opacity: 0;
	transition: all 0.4s ease-in-out;
}

.hamburger {
	width: 35px;
	height: 35px;
	margin-right: 10px;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	cursor: pointer;
	position: relative;
}

.hamburger span {
	width: 35px;
	height: 2px;
	background-color: #fff;
	position: absolute;
	transition: all 0.2s ease-in-out;
}

.top-line {
	transform: translateY(-12px);
}

.middle-line {
	opacity: 1;
	align-self: flex-start;
	width: 25px !important;
}

.bottom-line {
	transform: translateY(12px);
}

.active.top-line {
	transform: rotate(45deg);
}

.active.middle-line {
	opacity: 0;
}

.active.bottom-line {
	transform: rotate(-45deg);
}

nav.main.scroll,
nav.main.no-slider {
	transition: all 0.4s ease-in-out;
	height: 50px;
	margin: 0 auto;

	.menubar-background {
		opacity: 1;
		transition: all 0.4s ease-in-out;
	}

	.menubar-background.transparent {
		opacity: 0;
	}

	.nav-column5.menu {
		transform: translateX(20px);
	}

	.nav-column5.navbuttons {
		transform: translateX(-20px);
	}
}

nav.main.no-slider .logo-wrapper,
nav.main.scroll .logo-wrapper {
	:deep(.logo svg) {
		transform: translateY(-20px) scale(0.6);
		transform-origin: center;
		transition: all 0.4s ease-in-out;
	}
}

nav.main.no-slider .logo-wrapper.grow,
nav.main.scroll .logo-wrapper.grow {
	:deep(.logo svg) {
		transform: scale(1.2);
		transform-origin: center;
		transition: all 0.4s ease-in-out;
	}
}

.reserve-now {
	display: flex;
	flex-flow: row nowrap;
	justify-content: center;
	align-items: center;
	padding: 10px 25px 10px 20px;

	&:hover {
		background: #fff;
		color: var(--button-cta-color);
	}

	svg {
		margin: 0 12px 2px 0;
		display: none;
	}
}

.menu-button {
	background: none;
	padding: 0;
	margin: 0;
	font-size: 28px;
	font-weight: 400;
	cursor: pointer;
	text-align: left;
	display: flex;
	width: 100%;
	flex-flow: row nowrap;
	justify-content: start;
	align-items: end;
	gap: 10px;
	opacity: 0.85;
	color: #fff;

	span {
		font-family: var(--body-font-family);
	}

	&:hover {
		opacity: 1;
		background: none;
	}
}

nav .row,
.nav-buttons {
	justify-content: end;
	display: flex;
	flex-wrap: wrap;
	align-items: center;

	.nav-contact-icons {
		display: flex;
		flex-flow: row nowrap;
		align-items: center;
		gap: 20px;
		margin-right: 20px;

		a {
			color: #fff;
			font-size: 24px;
			opacity: 0.85;

			&:hover {
				opacity: 1;
				color: var(--cta-color);
			}
		}
	}
}

.nav-buttons {
	gap: 22px;
	color: #fff;
	animation: fade-from-right 0.7s ease;

	.lang-select {
		border-right: 1px solid var(--alternating-background-color);
		font-family: var(--heading-font-family);
		font-size: 24px;
	}

	a {
		text-decoration: none;
		margin: 0;
		opacity: 0.85;

		&.button,
		&:hover {
			opacity: 1;
		}
	}
}

.reserve-now i {
	display: none;
}

.nav-container {
	background: rgb(104 40 68 / 85%);
	backdrop-filter: blur(38px);
	padding: 30px 50px;
	position: fixed;
	top: 0;
	left: 0;
	padding-top: 80px;
	z-index: 101;
	width: 100%;
	height: 100%;
	opacity: 0;
	transition: all 0.4s ease-in-out;
	transform: translate(0, 100%) matrix(1, 0, 0, 1, 0, 0);

	.row {
		display: flex;
		flex-flow: column nowrap;
		justify-content: start;
		align-items: center;
		height: 100%;
	}
}

.nav-container.active {
	transform: matrix(1, 0, 0, 1, 0, 0);
	opacity: 1;
}

.lang-nav {
	display: none;
	list-style: none;
	text-align: left;
	line-height: 26px;
	padding-top: 40px;

	a {
		color: #fff;
		display: flex;
		flex-flow: row nowrap;
		align-items: center;
		justify-content: center;
		font-size: 16px !important;
		gap: 5px;

		&.router-link-exact-active,
		&:hover {
			color: var(--cta-color);
		}
	}

	img {
		max-width: 16px;
		float: left;
		margin: 0 5px 0 0;
	}
}

.mobile-lang-nav {
	margin: 40px 0 0;
	font-size: 14px;
	display: none;
}

.mobile-lang-nav img {
	max-width: 16px;
	margin: 0 10px 0 0;
}

.main-nav .mobile-lang-nav a {
	width: 100%;
	display: block;
	font-size: 16px;
	line-height: 28px;
}

/* Fullscreen Nav */
.main-nav {
	list-style: none;
	min-width: 25%;
	text-align: center;
	padding-top: 100px;
	height: 60%;
	margin: auto 0;

	a {
		color: #fff;
		font-size: 32px;
		line-height: 54px;
		font-weight: 400;
		font-family: var(--heading-font-family) !important;

		&:hover,
		&.active {
			color: var(--cta-color);
		}
	}
}

.nav-contact {
	width: 100%;
	display: flex;
	flex-flow: row wrap;
	align-items: center;
	justify-content: center;
	gap: 20px;
	margin-bottom: 100px;

	svg {
		margin-right: 5px;
	}

	.socials {
		margin: 0;
	}
}

.nav-contact > a {
	color: #fff;
	display: block;
	font-weight: 300;
	margin: 0;
}

.nav-contact > a i {
	margin: 0 10px 0 0;
}

.close-wrapper {
	align-self: flex-start;
}

.nav-close {
	color: #fff;
	cursor: pointer;
	font-size: 54px;
	padding: 0 0 0 12px;
}

/* Breadcrumbs */
.breadcrumbs ul {
	list-style: none;
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-start;
	align-items: center;
	margin: 0 0 75px;
}

.breadcrumbs li {
	margin: 0 10px 0 0;
}

.breadcrumbs i {
	font-size: 11px;
}

.breadcrumbs a {
	color: var(--body-color);
}

.breadcrumbs a.active {
	color: var(--cta-color);
}

@media (max-height: 740px) {
	.nav-container {
		overflow-y: scroll;
	}

	.lang-nav {
		position: relative;
		bottom: auto;
		left: auto;
		z-index: 1;
	}

	.main-nav {
		z-index: 2;
		position: relative;
	}

	.nav-contact {
		display: none;
	}
}

@media (max-width: 1080px) {
	.reserve-now {
		padding: 0;

		svg {
			margin: 0;
			display: inline-block;
		}
	}

	.nav-buttons .lang-select {
		display: none;
	}

	.lang-nav {
		display: block;
	}

	.close-wrapper {
		align-self: flex-end;
	}

	.nav-contact {
		display: none;
	}

	.main-nav {
		height: 90%;
	}
}

@media (max-width: 800px) {
	.lang-nav {
		position: relative;
		bottom: auto;
		left: auto;
		right: auto;
		margin-top: 30px;
	}

	.main-nav {
		a {
			font-size: 22px;
			line-height: 34px;
		}
	}

	.logo-wrapper {
		:deep(.logo svg) {
			transform: scale(1);
		}
	}

	nav.main.no-slider .logo-wrapper.grow,
	nav.main.scroll .logo-wrapper.grow {
		:deep(.logo svg) {
			transform: scale(1);
		}
	}
}

@media (max-width: 600px) {
	.main-nav,
	.contact-info,
	.lang-nav {
		width: 100%;
	}

	nav.main {
		.flex-row {
			padding: 0;
		}
	}

	.contact-info {
		margin: 25px auto 0;
	}

	.lang-nav {
		margin-top: 0;
	}

	nav .buttons .button {
		svg {
			margin-right: 0;
			font-size: 16px;
		}

		span {
			display: none;
		}
	}
}
</style>
